import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CarouselElement as CarouselType } from '../../types/element.types';

interface CarouselElementProps {
  element: CarouselType;
}

/**
 * Carousel Element Renderer
 * Image slider with navigation
 */
const CarouselElement: React.FC<CarouselElementProps> = ({ element }) => {
  const { slides } = element.content || { slides: [] };
  const { autoplay, interval, showArrows, showDots } = element.settings || {};
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (autoplay && slides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, interval || 3000);
      return () => clearInterval(timer);
    }
  }, [autoplay, interval, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  if (slides.length === 0) {
    return <div className="p-4 bg-gray-100 text-center text-gray-500">No slides</div>;
  }

  return (
    <div
      className="carousel-element relative"
      style={element.styles as React.CSSProperties}
    >
      {/* Slides */}
      <div className="relative overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`transition-opacity duration-500 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0 absolute inset-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.caption || `Slide ${index + 1}`}
              className="w-full h-auto"
            />
            {slide.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4">
                {slide.caption}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {showArrows && slides.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 transition-all"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots */}
      {showDots && slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-4' : 'bg-white bg-opacity-50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CarouselElement;

